import type {
  LoginRequest,
  RegisterRequest,
  TokenResponse,
  UpdatePasswordRequest,
  UserResponse,
} from "../../../core/backend/clients/backendClient";
import { backendService } from "../../../core/backend/services/backendService";
import { jwtService } from "../../../core/services/jwtService";
import { logService } from "../../../core/services/logService";
import { User } from "../models/userModel";

class AuthService {
  login(loginRequest: LoginRequest) {
    return backendService.createBackendClient().apiAuthLogin(loginRequest);
  }

  loginByToken(email: string, token: string) {
    if (!email || !token) return;

    let user: User = new User();

    this.createUserFromToken(user, email, token);

    logService.debug("loginByToken", email, token, user);

    return user;
  }

  mapUser(user: User, email: string, tokenResponse: TokenResponse) {
    if (!user || !tokenResponse.token) return;

    this.createUserFromToken(user, email, tokenResponse.token);
    logService.debug("mapUser", email, user);
  }

  createUserFromToken(user: User, email: string, token: string) {
    if (!user || !token) return;

    if (jwtService.isTokenExpired(token)) {
      logService.debug("createUserFromToken 2 - isTokenExpired");
      return;
    }

    const decodedToken = jwtService.decodeToken(token);

    user.id = Number.parseInt(decodedToken.sub);
    user.email = email;
    user.token = token;
    user.expiresAt = jwtService.getTokenExpirationDate(token) ?? undefined;
    user.isLoggedIn =
      !!token && !!user.expiresAt && user.expiresAt > new Date();
  }

  getUserInfo() {
    return backendService.createBackendClient().apiUserGet();
  }

  setUserInfo(user: User, response: UserResponse) {
    user.name = response.name ?? "";
    user.birthDate = response.birthDate;
    user.gender = response.gender;
    user.ssn = response.ssn ?? "";
    user.ssnConsent = response.ssnConsent ?? false;
    user.address = response.address ?? {};
    user.phone = response.phone ?? "";
  }

  register(request: RegisterRequest) {
    return backendService.createBackendClient().apiAuthRegister(request);
  }

  forgottenPassword(email: string) {
    return backendService.createBackendClient().apiAuthForgottenPassword(email);
  }

  activate(key: string) {
    return backendService.createBackendClient().apiAuthActivate(key);
  }

  resetPassword(request: UpdatePasswordRequest) {
    return backendService.createBackendClient().apiAuthUpdatePassword(request);
  }
}

export const authService = new AuthService();
